import { computed, reactive, Ref, ref, watch } from '@vue/composition-api';
import { DataOptions } from 'vuetify';
import debounce from 'lodash/debounce';
import { VideoResponse, DatasetListingResponse, VideoList, DatasetListing, ModelStatisticsResponse, ModelStatistics, FauModelStatistics } from '../types';
import store from '@/store';
import { TEST_SERVER_URL } from '@/environment';
import { Client } from '@/api';

export function useClient(): Client {
  const { client } = store.state;
  if (client === null) {
    throw new Error('Client not initialized!');
  }

  return client;
}

/**
 * Returns a ref that tracks whether the current
 * client is connected to the test or prod server
 */
export function useCurrentServer() {
  // Dont' use useClient, as we want reactive state from the store
  const isTestServer = computed(() => {
    if (store.state.client === null) {
      return undefined;
    }

    return store.state.client.serverURL === TEST_SERVER_URL;
  });

  return { isTestServer };
}

export function useDatasets() {
  const loading = ref(false);
  const items = ref([]) as Ref<DatasetListing[]>;
  const selected = ref([]) as Ref<DatasetListing[]>;
  const search = ref('');
  const filterStudyType = ref('');
  const filterSubjectType = ref('');
  const filterValidationOnly = ref('');
  const onlyShowFlagged = ref(false);
  const itemsPerPage = [5, 10, 15, 50];
  const datasetTableOptions = ref<DataOptions>(reactive({
    page: 1,
    itemsPerPage: 15,
    sortBy: ['created_at'],
    sortDesc: [true],
    groupBy: [],
    groupDesc: [],
    multiSort: false,
    mustSort: true,
  }));

  const dataTableInfo = ref<DatasetListingResponse>({
    limit: datasetTableOptions.value.itemsPerPage,
    page: 1,
    total_number: 0,
    total_pages: 0,
    datasets_meta: [],
  });

  async function fetch() {
    const { client } = store.state;
    if (client === null) {
      throw new Error('Client not initialized!');
    }
    loading.value = true;
    dataTableInfo.value = await client.getRemoteDatasets(datasetTableOptions.value, search.value, filterStudyType.value, filterSubjectType.value, filterValidationOnly.value, onlyShowFlagged.value);
    items.value = dataTableInfo.value.datasets_meta.map((entry) => ({
      ...entry,
      subject_type: (entry.subject_type || '').toUpperCase(),
    }))
      .map((dataset) => ({ ...dataset }));

    loading.value = false;
  }

  watch(datasetTableOptions, fetch, { deep: true });
  watch(search, debounce(fetch, 200), { deep: true });
  watch(filterStudyType, fetch, { deep: true });
  watch(filterSubjectType, fetch, { deep: true });
  watch(filterValidationOnly, fetch, { deep: true });
  watch(onlyShowFlagged, (val, previous) => {
    // If changed, set to first page, to ensure consistency
    if (val !== previous) {
      datasetTableOptions.value.page = 1;
    }

    fetch();
  });

  return {
    loading,
    search,
    filterStudyType,
    filterSubjectType,
    filterValidationOnly,
    items,
    selected,
    fetch,
    datasetTableOptions,
    dataTableInfo,
    itemsPerPage,
    onlyShowFlagged,
  };
}

/**
 * Return state for video management.
 */
export function useVideos() {
  // TODO: If splitting up tabs by source, refactor useSystemUserTabs and use
  const loading = ref(false);
  const search = ref('');
  const filterSubjectType = ref('');
  const filterSubjectSex = ref('');
  const filterDataSource = ref('');
  const items = ref([]) as Ref<VideoList[]>;
  const selected = ref<VideoList[]>([]) as Ref<VideoList[]>;
  const itemsPerPage = [5, 10, 15, 50];
  const videoTableOptions = ref<DataOptions>(reactive({
    page: 1,
    itemsPerPage: 15,
    sortBy: ['name'],
    sortDesc: [true],
    groupBy: [],
    groupDesc: [],
    multiSort: false,
    mustSort: false,
  }));
  const videoTableInfo = ref<VideoResponse>({
    limit: videoTableOptions.value.itemsPerPage,
    page: 1,
    total_number: 0,
    total_pages: 0,
    videos: [],
  });
  let newestRequestId = 0;
  const inFlightRequests = new Map<string, Promise<VideoResponse>>();

  function requestKey() {
    return JSON.stringify({
      options: videoTableOptions.value,
      search: search.value,
      subjectType: filterSubjectType.value,
      subjectSex: filterSubjectSex.value,
      dataSource: filterDataSource.value,
    });
  }

  async function fetch() {
    const client = useClient();
    const requestId = ++newestRequestId;
    const key = requestKey();
    loading.value = true;
    try {
      let request = inFlightRequests.get(key);
      if (!request) {
        request = client.getRemoteVideos(
          videoTableOptions.value,
          search.value,
          filterSubjectType.value,
          filterSubjectSex.value,
          filterDataSource.value,
        );
        inFlightRequests.set(key, request);
      }
      const response = await request;
      // A slower older request must not overwrite the latest table state.
      if (requestId !== newestRequestId) return;
      videoTableInfo.value = response;
      items.value = response.videos.map((video) => ({
        ...video,
        subject_type: (video.subject_type || '').toUpperCase(),
      }));
    } finally {
      const request = inFlightRequests.get(key);
      if (request) {
        inFlightRequests.delete(key);
      }
      if (requestId === newestRequestId) loading.value = false;
    }
  }

  // Watch options and search
  watch(videoTableOptions, fetch, { deep: true });
  watch(search, debounce(fetch, 200), { deep: true });
  watch(filterSubjectType, fetch, { deep: true });
  watch(filterSubjectSex, fetch, { deep: true });
  watch(filterDataSource, fetch, { deep: true });

  return {
    loading,
    search,
    filterSubjectType,
    filterSubjectSex,
    filterDataSource,
    items,
    selected,
    fetch,
    videoTableOptions,
    videoTableInfo,
    itemsPerPage,
  };
}

/**
 * Return state for ML model statistics
 */
export function useModelstatistics() {
  const loading = ref(false);
  const selectedSubject = ref<string>('general') as Ref<string>;
  const selectedStudyType = ref<string>('pain-mgs') as Ref<string>;
  const selectedModel = ref<string>('default') as Ref<string>;
  const resultsTemplate = ref<FauModelStatistics>({
    results: {
      accuracy: 0,
      f1: 0,
      precision: 0,
      sensitivity: 0,
    },
    test_confusion_matrix: [[0, 0, 0], [0, 0, 0], [0, 0, 0]],
  });
  const modelStatistics = ref<ModelStatistics>({
    ears: resultsTemplate.value,
    orbital: resultsTemplate.value,
    nose: resultsTemplate.value,
    whiskers: resultsTemplate.value,
    cheek: resultsTemplate.value,
  });
  const statisticsJson = ref<ModelStatisticsResponse>({
    subject_type: 'general',
    study_type: 'pain-mgs',
    model_id: 'default',
    statistics: modelStatistics.value,
  });
  async function fetch() {
    const client = useClient();

    loading.value = true;

    const modelStatisticsResponse: ModelStatisticsResponse = await client.getModelStatistics(selectedSubject.value, selectedStudyType.value, selectedModel.value);
    statisticsJson.value = modelStatisticsResponse;
    modelStatistics.value = modelStatisticsResponse.statistics;
    loading.value = false;
  }

  watch(selectedModel, fetch);

  return {
    loading,
    selectedSubject,
    selectedStudyType,
    selectedModel,
    fetch,
    modelStatistics,
    statisticsJson,
  };
}
